// src/Services/commonRequest.js

import axios from "axios";
import { getToken, removeToken, isTokenExpired } from "./tokenService";

// 1. Create a dedicated Axios instance.
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Reads the Authorization header off a request config.
 *
 * Axios 1.x wraps headers in an AxiosHeaders instance (use .get); older versions
 * and some interceptor paths hand back a plain object. Handle both rather than
 * assuming, because getting this wrong silently re-introduces the bug below.
 *
 * @param {object} config
 * @returns {string|null}
 */
const getAuthHeader = (config) => {
  if (!config || !config.headers) return null;
  const h = config.headers;
  if (typeof h.get === "function") {
    return h.get("Authorization") || h.get("authorization") || null;
  }
  return h.Authorization || h.authorization || null;
};

// 2. Set up a request interceptor to add the token to every request.
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 3. Response interceptor.
apiClient.interceptors.response.use(
  (response) => {
    // If the request succeeds, just return the response
    return response;
  },
  (error) => {
    // ADDED Nov 2026 (security release): sign-out never needs handling here.
    if (error.config && typeof error.config.url === "string" && error.config.url.endsWith("/login/logout")) {
      return Promise.reject(error);
    }

    // ADDED Nov 2026 (security release): the server ended the session (30 minutes
    // idle, 12-hour maximum, signed out elsewhere, or an administrator changed this
    // account). The token may still look valid in the browser, so the check below
    // would not catch it. Go to the login page with the reason.
    const sessionCode = error.response && error.response.status === 401 && error.response.data && error.response.data.code;
    if (sessionCode && String(sessionCode).startsWith("SESSION_")) {
      removeToken();
      const reason = sessionCode === "SESSION_IDLE" ? "idle" : sessionCode === "SESSION_MAX" ? "max" : "ended";
      if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
        window.location.href = `/?reason=${reason}`;
      }
      return new Promise(() => {});
    }

    if (error.response && error.response.status === 401) {
      const token = getToken();

      // CHANGED (09-03): a 401 is only a session expiry if the request actually
      // carried a token.
      //
      // The unauthenticated flows - registration OTP verify, login, forgot
      // password, reset password, user activation - all run before a token
      // exists. Under the previous version !token was TRUE for every one of
      // them, so a legitimate "wrong code" or "bad credentials" 401 was treated
      // as an expired session: the caller's .catch() never ran (the returned
      // promise never resolves), no error was ever shown, and the submit button
      // stayed spinning. On /register that looked like nothing happening at all;
      // on /login there was not even a redirect to hint at what went wrong,
      // because pathname was already "/login".
      //
      // No Authorization header means the server rejected the credentials in the
      // body, not the session. That belongs to the component.
      const requestWasAuthenticated = Boolean(getAuthHeader(error.config));

      if (requestWasAuthenticated && (!token || isTokenExpired(token))) {
        console.warn("Session expired (401 received). Logging out...");

        // CHANGED: removeToken() instead of localStorage.clear().
        //
        // localStorage is shared across every tab on this origin, so clear() from
        // one stale background tab wiped the session in all of them - and took
        // every other stored value with it. Remove only the token.
        removeToken();

        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }

        // Return a Promise that never resolves, so the calling component does not
        // enter its .catch() and show an error toast on top of the redirect.
        return new Promise(() => {});
      }

      if (requestWasAuthenticated) {
        // Token still looks valid: this 401 came from something else. Let the
        // caller handle it rather than silently destroying the session.
        console.warn("401 received on a request while the session token is still valid.", {
          url: error.config && error.config.url,
        });
      } else {
        // Unauthenticated endpoint. Bad OTP, bad password, expired reset link.
        console.warn("401 received on an unauthenticated request; passing to caller.", {
          url: error.config && error.config.url,
        });
      }
    }

    // Anything else (400, 403, 500, or a 401 the caller should handle) goes to
    // the component.
    return Promise.reject(error);
  }
);

export default apiClient;
